import React, { useState } from "react";

import {
  AlertTriangle,
  ShieldCheck,
  MoreVertical,
  User,
  Dices,
  Gauge,
  Wrench,
  CircleHelp,
  Zap,
  TestTubeDiagonal,
  MountainSnow,
  Radiation,
  Dna,
  Hand,
  Earth,
  ThermometerSun,
  Settings,
  RefreshCw,
  SendToBack,
  MapPinned,
  NotebookTabs,
  Package,
} from "lucide-react";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../components/ui/accordion";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";

import RiskBadge from "../../components/Risk Matrix/RiskBadge";
import { useToast } from "../../components/hooks/use-toast";

import { cn } from "../../lib/utils";

import { mitigationStatusColors } from "../../helpers/mitigationHelper";
import { riskStatusColors } from "../../helpers/riskHelper";

import hazardService from "../../services/hazardService";
import firestoreService from "../../services/firestoreService";

const HazardCard = React.memo(({ project, hazard, startHazardEditing }) => {
  const minItemsShowing = 3;
  const minMitigationsShowing = 2;

  const [showAllCauses, setShowAllCauses] = useState(false);
  const [showAllConsequences, setShowAllConsequences] = useState(false);
  const [showAllMitigations, setShowAllMitigations] = useState(false);

  const { toast } = useToast();

  const updateRiskStatus = async (newStatus) => {
    try {
      await hazardService.updateHazard(project, {
        ...hazard,
        riskStatus: newStatus,
      });
    } catch (error) {
      console.error("Failed to update hazard.", error);

      toast({
        variant: "destructive",
        title: "Failed to update hazard.",
        description: error.message,
      });
    }
  };

  const deleteHazard = async () => {
    try {
      await firestoreService.delete(
        `projects/${project.id}/hazards`,
        hazard.id
      );
    } catch (error) {
      console.error("Failed to delete hazard.", error);

      toast({
        variant: "destructive",
        title: "Failed to delete hazard.",
        description: error.message,
      });
    }
  };

  const formatTextWithLineBreaks = (text) => {
    if (!text) return "";

    // Handle legacy string format
    return text.split("\n").map((line, index) => {
      const trimmedLine = line.trim();
      if (/^\d+\./.test(trimmedLine) || /^[-•*]/.test(trimmedLine)) {
        return (
          <React.Fragment key={index}>
            {trimmedLine}
            <br />
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      );
    });
  };

  const renderHazardTypeIcon = (hazardType) => {
    let icon;

    switch (hazardType) {
      case "Mechanical":
        icon = <Wrench className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      case "Electrical":
        icon = <Zap className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      case "Chemical":
        icon = (
          <TestTubeDiagonal className="h-6 w-6 ml-2 mr-4 cursor-pointer" />
        );
        break;

      case "Geological":
        icon = <MountainSnow className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      case "Biological":
        icon = <Dna className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      case "Radiation":
        icon = <Radiation className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      case "Human-made":
        icon = <Hand className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      case "Environmental":
        icon = <Earth className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      case "Thermal":
        icon = <ThermometerSun className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;

      default:
        icon = <CircleHelp className="h-6 w-6 ml-2 mr-4 cursor-pointer" />;
        break;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{icon}</TooltipTrigger>
        <TooltipContent>
          <p>Hazard Type: {hazardType}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  const renderBadges = (items) => {
    const itemArray = Array.isArray(items) ? items : [items];

    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {itemArray.length > 0 ? (
          <>
            {itemArray.map((item, index) => (
              <div
                key={index}
                className="badge bg-gray-200 px-2 rounded-full inline-block"
              >
                <span className="text-gray-800">{item}</span>
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="badge bg-gray-200 px-2 rounded-full inline-block">
              <span className="text-gray-800">N/A</span>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Card key={hazard.id} id={`hazard-${hazard.hazardId}`} className="w-full shadow-sm">
      <div
        className={`sticky top-0 z-30 ${riskStatusColors[hazard.riskStatus]} ${
          hazard.riskStatus === "Cancelled" ? "opacity-50" : ""
        }`}
      >
        <CardHeader className="p-2">
          <CardTitle className="flex justify-between items-center">
            <div className="flex-shrink-0 flex flex-col">
              {renderHazardTypeIcon(hazard.type)}
            </div>

            <div className="flex-grow flex flex-col">
              <span className="text-base font-bold hazard-id">{`${hazard.hazardId}`}</span>

              <div className="flex items-center">
                {hazard.hazardSource && (
                  <span className="text-base text-neutral-800 hazard-source">
                    {hazard.hazardSource}:
                  </span>
                )}

                <span
                  className={cn(
                    "text-base text-gray-500 hazard-description",
                    hazard.hazardSource ? "ml-1" : ""
                  )}
                >
                  {hazard.description}
                </span>
              </div>

              {/* <span className="text-xs italic text-gray-500">{`Risk Type: ${hazard.riskCategory}`}</span>
                  <span className="text-xs italic text-gray-500">{`From: ${hazard.meetings}`}</span> */}
            </div>

            <div className="flex items-center space-x-1">
              <Select
                onValueChange={(value) => updateRiskStatus(value)}
                defaultValue={hazard.riskStatus}
              >
                <SelectTrigger className="w-[120px] bg-white text-xs h-8">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>

                <SelectContent>
                  {Object.keys(riskStatusColors).map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={startHazardEditing}>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => deleteHazard(hazard.id)}>
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardTitle>
        </CardHeader>
      </div>

      <CardContent className="bg-white p-2">
        {/* Description section */}
        <div className="grid grid-cols-6 gap-1 text-sm mb-4">
          <div className="bg-gray-50 p-2 rounded">
            <div className="flex items-center space-x-1">
              <Settings className="h-4 w-4 mr-1" />
              <span className="font-semibold text-gray-600">System: </span>
            </div>

            {renderBadges(hazard.systems)}
          </div>

          <div className="bg-gray-50 p-2 rounded">
            <div className="flex items-center space-x-1">
              <SendToBack className="h-4 w-4 mr-1" />

              <span className="font-semibold text-gray-600">
                Operating Mode:{" "}
              </span>
            </div>

            {renderBadges(hazard.operatingModes)}
          </div>

          <div className="bg-gray-50 p-2 rounded">
            <div className="flex items-center space-x-1">
              <MapPinned className="h-4 w-4 mr-1" />
              <span className="font-semibold text-gray-600">Location: </span>
            </div>

            {renderBadges(hazard.locations)}
          </div>

          <div className="bg-gray-50 p-2 rounded">
            <div className="flex items-center space-x-1">
              <RefreshCw className="h-4 w-4 mr-1" />
              <span className="font-semibold text-gray-600">
                Project Lifecycle:{" "}
              </span>
            </div>

            {renderBadges(hazard.projectLifecycles)}
          </div>

          <div className="bg-gray-50 p-2 rounded">
            <div className="flex items-center space-x-1">
              <NotebookTabs className="h-4 w-4 mr-1" />
              <span className="font-semibold text-gray-600">Discipline: </span>
            </div>

            {renderBadges(hazard.disciplines)}
          </div>

          <div className="bg-gray-50 p-2 rounded">
            <div className="flex items-center space-x-1">
              <Package className="h-4 w-4 mr-1" />
              <span className="font-semibold text-gray-600">
                Drawing Package:{" "}
              </span>
            </div>

            {renderBadges(hazard.drawingPackages)}
          </div>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-3 gap-2 text-sm">
          {/* Unmitigated Risk */}
          <div className="p-2 rounded bg-gray-100">
            <div className="grid grid-cols-2">
              <div className="col-span-2 sm:col-span-1">
                <div className="flex items-center space-x-1">
                  <AlertTriangle className="h-4 w-4 mr-1 mb-1" />
                  <h3 className="font-semibold mb-1">Unmitigated Risk</h3>
                </div>
              </div>

              <div className="flex justify-end col-span-1">
                <div className="flex items-center space-x-1">
                  <RiskBadge
                    riskMatrixName={project.riskMatrix}
                    probability={hazard.unmitigatedRisk.probability}
                    severity={hazard.unmitigatedRisk.severity}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1 cursor-pointer">
                    <Dices className="h-4 w-4" />

                    <span>
                      {hazard.unmitigatedRisk.probability
                        ? hazard.unmitigatedRisk.probability
                        : "N/A"}
                    </span>
                  </div>
                </TooltipTrigger>

                <TooltipContent>
                  <p>Probability</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1 ml-4 cursor-pointer">
                    <Gauge className="h-4 w-4" />
                    <span>
                      {hazard.unmitigatedRisk.severity
                        ? hazard.unmitigatedRisk.severity
                        : "N/A"}
                    </span>
                  </div>
                </TooltipTrigger>

                <TooltipContent>
                  <p>Severity</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Affected User */}
          <div className="p-2 rounded bg-gray-100">
            <div className="flex items-center space-x-1">
              <User className="h-4 w-4 mr-1 mb-1" />
              <h3 className="font-semibold mb-1">Affected User</h3>
            </div>

            <div className="flex items-center space-x-1">
              {renderBadges(hazard.users)}
            </div>
          </div>

          {/* Retained Risk */}
          <div className="p-2 rounded bg-gray-100">
            <div className="grid grid-cols-2">
              <div className="col-span-2 sm:col-span-1">
                <div className="flex items-center space-x-1">
                  <ShieldCheck className="h-4 w-4 mr-1 mb-1" />
                  <h3 className="font-semibold mb-1">Retained Risk</h3>
                </div>
              </div>

              <div className="flex justify-end col-span-1">
                <div className="flex items-center space-x-1">
                  <RiskBadge
                    riskMatrixName={project.riskMatrix}
                    probability={hazard.retainedRisk.probability}
                    severity={hazard.retainedRisk.severity}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1 cursor-pointer">
                    <Dices className="h-4 w-4" />

                    <span>
                      {hazard.retainedRisk.probability
                        ? hazard.retainedRisk.probability
                        : "N/A"}
                    </span>
                  </div>
                </TooltipTrigger>

                <TooltipContent>
                  <p>Probability</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1 ml-4 cursor-pointer">
                    <Gauge className="h-4 w-4" />
                    <span>
                      {hazard.retainedRisk.severity
                        ? hazard.retainedRisk.severity
                        : "N/A"}
                    </span>
                  </div>
                </TooltipTrigger>

                <TooltipContent>
                  <p>Severity</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Sticky Causes & Consequences */}
        <div className="grid grid-cols-3 gap-1">
          {/* Causes */}
          <div className="bg-gray-50 p-2 rounded mb-1">
            <h3 className="text-sm font-semibold text-gray-700 ml-2 my-2">
              Causes
            </h3>

            {hazard.causes.length > 0 ? (
              <div className="space-y-2">
                {(showAllCauses
                  ? hazard.causes
                  : hazard.causes.slice(0, minItemsShowing)
                ).map((cause, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-md border p-3 text-sm"
                  >
                    <span className="text-gray-600">
                      {typeof cause === "string" ? cause : cause.description}
                    </span>
                  </div>
                ))}

                {hazard.causes.length > minItemsShowing && (
                  <div className="flex justify-end">
                    <Badge
                      className="bg-white cursor-pointer"
                      variant="outline"
                      onClick={() => setShowAllCauses(!showAllCauses)}
                    >
                      {showAllCauses
                        ? "See less"
                        : `+${hazard.causes.length - minItemsShowing}`}
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic text-sm ml-2">
                No causes identified.
              </p>
            )}
          </div>

          {/* Consequences */}
          <div className="bg-gray-50 p-2 rounded">
            <h3 className="text-sm font-semibold text-gray-700 ml-2 my-2">
              Consequences
            </h3>

            {hazard.consequences.length > 0 ? (
              <div className="space-y-2">
                {(showAllConsequences
                  ? hazard.consequences
                  : hazard.consequences.slice(0, minItemsShowing)
                ).map((consequence, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-md border p-3 text-sm shadow-sm"
                  >
                    <span className="text-gray-600">{consequence}</span>
                  </div>
                ))}

                {hazard.consequences.length > minItemsShowing && (
                  <div className="flex justify-end">
                    <Badge
                      className="bg-white cursor-pointer"
                      variant="outline"
                      onClick={() =>
                        setShowAllConsequences(!showAllConsequences)
                      }
                    >
                      {showAllConsequences
                        ? "See less"
                        : `+${hazard.consequences.length - minItemsShowing}`}
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic text-sm ml-2">
                No consequences identified.
              </p>
            )}
          </div>

          {/* Mitigations */}
          <div className="bg-gray-50 p-2 rounded">
            <h3 className="text-sm font-semibold text-gray-700 ml-2 my-2">
              Mitigations
            </h3>

            {hazard.mitigations.length > 0 ? (
              <div className="space-y-2">
                {(showAllMitigations
                  ? hazard.mitigations
                  : hazard.mitigations.slice(0, minMitigationsShowing)
                ).map((mitigation, index) => (
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full mb-2"
                    key={index}
                  >
                    <AccordionItem
                      value={`item-${index}`}
                      className={`${
                        mitigationStatusColors[mitigation.status] ||
                        "bg-gray-50"
                      } rounded-md overflow-hidden`}
                    >
                      <AccordionTrigger className="text-sm py-2 px-3 hover:no-underline hover:bg-opacity-80">
                        <div className="flex flex-col items-start w-full text-left">
                          <span className="font-semibold">
                            {mitigation.type || "N/A"}
                          </span>

                          <span className="text-gray-600">
                            {mitigation.description || "No description"}
                          </span>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-3 py-2 bg-white bg-opacity-60">
                        <div className="text-xs">
                          <p className="mb-1">
                            <strong>ID:</strong>{" "}
                            {mitigation.mitigationId || "N/A"}
                          </p>

                          <p className="mb-1">
                            <strong>Owner:</strong> {mitigation.owner || "N/A"}
                          </p>

                          <p className="mb-1">
                            <strong>Status:</strong>{" "}
                            {mitigation.status || "N/A"}
                          </p>

                          <p className="mb-1">
                            <strong>Due Date:</strong>{" "}
                            {mitigation.dueDate
                              ? new Date(
                                  mitigation.dueDate
                                ).toLocaleDateString()
                              : "N/A"}
                          </p>

                          <p className="mb-1">
                            <strong>Safety Requirement:</strong>{" "}
                            {mitigation.safetyRequirement || "N/A"}
                          </p>

                          <p>
                            <strong>Evidence:</strong>{" "}
                            {mitigation.evidence ? (
                              <Button
                                variant="link"
                                onClick={() =>
                                  window.open(mitigation.evidence, "_blank")
                                }
                                className="text-blue-500 hover:text-blue-600 p-0"
                              >
                                View Evidence
                              </Button>
                            ) : (
                              "N/A"
                            )}
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ))}

                {hazard.mitigations.length > minMitigationsShowing && (
                  <div className="flex justify-end">
                    <Badge
                      className="bg-white cursor-pointer"
                      variant="outline"
                      onClick={() => setShowAllMitigations(!showAllMitigations)}
                    >
                      {showAllMitigations
                        ? "See less"
                        : `+${
                            hazard.mitigations.length - minMitigationsShowing
                          }`}
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic text-sm ml-2">
                No mitigations proposed.
              </p>
            )}
          </div>
        </div>
      </CardContent>

      <CardFooter className="bg-gray-50 p-2">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="item-1">
            <AccordionTrigger className="text-sm">
              Safety Statement and Residual Risk
            </AccordionTrigger>

            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <h3 className="font-semibold mb-1 text-gray-700">
                    Safety Statement
                  </h3>

                  <p className="text-gray-600">
                    {formatTextWithLineBreaks(hazard.sfairpStatement)}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-1 text-gray-700">
                    Description of Residual Risk
                  </h3>

                  <p className="text-gray-600">
                    {formatTextWithLineBreaks(hazard.residualRiskDescription)}
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardFooter>
    </Card>
  );
});

export default HazardCard;
